import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useOutletContext } from "react-router-dom";
import { IntegrationShell } from "V2.0/HomePages/Integrations/common/Shell";
import InputComponent from "V2.0/components/InputComponent";
import ExotelLogo from "./ExotelLogo";
import { Button } from "V2.0/common/button";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import useExotelStore from "./Store";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

function Exotel(props) {
  const { workspaceId, updateExotelSettings, exotelSettings, isViewOnly } =
    props;

  const {
    state,
    updateAPIKey,
    updateAPIToken,
    updateAccountSid,
    updateSubdomain,
    updateState,
    updateVoiceBotAppId,
    updatePhoneNumber,
    addPhoneNumber,
    removePhoneNumber,
  } = useExotelStore(exotelSettings);

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(
    function () {
      updateState(exotelSettings);
    },
    [exotelSettings, workspaceId],
  );

  const PhoneNumbers = useMemo(
    function () {
      if (state?.phoneNumbers.length === 1) {
        const idx = 0;
        const phoneNumber = state.phoneNumbers[idx];

        return (
          <InputComponent
            type="text"
            value={phoneNumber}
            // error={state.errors.phoneNumbers[idx]}
            invalid={Boolean(state.errors.phoneNumbers[idx])}
            onChange={(e) => updatePhoneNumber(e.target.value, idx)}
            disabled={isViewOnly}
          />
        );
      }

      return state?.phoneNumbers?.map(function (phoneNumber, idx) {
        const handlePhoneNumberChange = function (e) {
          updatePhoneNumber(e.target.value, idx);
        };
        const handleRemovePhoneNumber = function (e) {
          e.stopPropagation();
          removePhoneNumber(idx);
        };
        return (
          <div className="flex items-center gap-x-2 w-full">
            <div className="w-full">
              <InputComponent
                type="text"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                invalid={Boolean(state.errors.phoneNumbers[idx])}
                disabled={isViewOnly}
              />
            </div>
            {!isViewOnly && (
              <div onClick={handleRemovePhoneNumber}>
                <XMarkIcon className="size-4 hover:cursor-pointer" />
              </div>
            )}
          </div>
        );
      });
    },
    [state.phoneNumbers, isViewOnly],
  );

  const isConnectDisabled = useMemo(
    function () {
      const { phoneNumbers, errors, ...restState } = state;

      const isRestStateHaveEmptyValues = Object.values(restState).some(
        (value) => !value,
      );

      const isPhoneNumbersEmpty = Object.values(phoneNumbers).some(
        (value) => !value,
      );

      return isRestStateHaveEmptyValues || isPhoneNumbersEmpty;
    },
    [state],
  );

  const handleConnect = function () {
    const lifecycleCbs = {
      init: () => {
        setLoading(true);
        message.loading("Connecting");
      },
      success: () => {
        message.destroy();
        message.success("Connected");
        setLoading(false);
      },
      error: (e) => {
        message.destroy();
        setLoading(false);
        showErrorToast(e, "Failed to Connect");
      },
    };

    const { errors, ...restData } = state;

    const payload = {
      telephonySettings: {
        telephonyProvider: "exotel",
        exotelSettings: restData,
      },
    };

    updateExotelSettings(payload, workspaceId, lifecycleCbs);
  };

  return (
    <div className="mt-4 w-full space-y-4">
      <div className="flex items-center gap-x-2">
        <ExotelLogo />
        <p className="text-base font-semibold leading-6 text-gray-900">
          Exotel
        </p>
      </div>
      <div className="p-8 border rounded-lg space-y-4">
        <InputComponent
          type="text"
          label="API Key *"
          value={state.apiKey}
          // error={state.errors.apiKey}
          invalid={Boolean(state.errors.apiKey)}
          onChange={(e) => updateAPIKey(e.target.value)}
          disabled={isViewOnly}
        />
        <InputComponent
          type="text"
          label="API Token *"
          value={state.apiToken}
          // error={state.errors.apiToken}
          invalid={Boolean(state.errors.apiToken)}
          onChange={(e) => updateAPIToken(e.target.value)}
          disabled={isViewOnly}
        />
        <InputComponent
          type="text"
          label="Account SID *"
          value={state.accountSid}
          // error={state.errors.accountSid}
          invalid={Boolean(state.errors.accountSid)}
          onChange={(e) => updateAccountSid(e.target.value)}
          disabled={isViewOnly}
        />
        <InputComponent
          type="text"
          label="Sub Domain *"
          value={state.subdomain}
          // error={state.errors.subdomain}
          invalid={Boolean(state.errors.subdomain)}
          onChange={(e) => updateSubdomain(e.target.value)}
          disabled={isViewOnly}
        />
        <InputComponent
          type="text"
          label="Voicebot App ID *"
          value={state.voiceBotAppId}
          // error={state.errors.voiceBotAppId}
          invalid={Boolean(state.errors.voiceBotAppId)}
          onChange={(e) => updateVoiceBotAppId(e.target.value)}
          disabled={isViewOnly}
        />
        <div>
          <p className="text-sm font-medium">Phone Numbers</p>
          {PhoneNumbers}
          <div>
            <Button
              plain
              onClick={addPhoneNumber}
              disabled={isViewOnly}
            >
              <PlusIcon /> Add Phone Number
            </Button>
          </div>
        </div>

        {!isViewOnly && (
          <Button
            className="w-full"
            disabled={isConnectDisabled}
            onClick={handleConnect}
            loading={loading}
          >
            Connect
          </Button>
        )}
      </div>
    </div>
  );
}

export default function ExotelIntegration() {
  const {
    company,
    projectId,
    thirdpartyData,
    isThirdPartyLoading,
    updateThirdpartyData,
    fetchThirdpartyData,
    isViewOnly,
  } = useOutletContext();

  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (
      company &&
      !company?.thirdPartyIntegrationVisibility.showExotelIntegration
    ) {
      navigate("/integrations", { replace: true });
    }
  }, [company]);

  if (!company) {
    return null;
  }

  if (
    company &&
    company?.thirdPartyIntegrationVisibility &&
    !company.thirdPartyIntegrationVisibility.showExotelIntegration
  ) {
    return (
      <Navigate
        to="/integrations"
        replace
      />
    );
  }

  if (isThirdPartyLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <AiOutlineLoading3Quarters className="animate-spin" />
      </div>
    );
  }

  return (
    <IntegrationShell>
      <Exotel
        workspaceId={projectId}
        exotelSettings={thirdpartyData?.telephonySettings?.exotelSettings}
        updateExotelSettings={updateThirdpartyData}
        isViewOnly={isViewOnly}
      />
    </IntegrationShell>
  );
}
